import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Category → label + MUI chip color. UNPAID is a derived (no-payment) row.
const CATEGORY_META = {
  PAID: { label: 'Paid', color: 'success' },
  PARTIAL: { label: 'Partial', color: 'warning' },
  SHORT: { label: 'Short', color: 'error' },
  DONATION: { label: 'Donation', color: 'info' },
  UNPAID: { label: 'Unpaid', color: 'default' },
};

function formatAmount(amount) {
  if (amount == null || amount === '') return '—';
  return `R ${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

function CategoryChip({ category }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.UNPAID;
  return <Chip label={meta.label} color={meta.color} size="small" variant={category === 'UNPAID' ? 'outlined' : 'filled'} />;
}

const PaymentTimeline = ({
  payments = [],
  memberSince,
  onRecordPayment,
  onEditPayment,
  onDeletePayment,
  estateBilling = false,
  canManage = true,
}) => {
  const currentYear = new Date().getFullYear();
  const startYear = memberSince ? new Date(memberSince).getFullYear() : currentYear;

  // Group payments by year (a year can have several transactions now).
  const paymentsByYear = {};
  payments.forEach((p) => {
    if (!paymentsByYear[p.year]) paymentsByYear[p.year] = [];
    paymentsByYear[p.year].push(p);
  });

  // Years to display: membership span ∪ any year that has a payment, newest first.
  const yearSet = new Set();
  for (let y = startYear; y <= currentYear; y++) yearSet.add(y);
  payments.forEach((p) => p.year && yearSet.add(p.year));
  const years = [...yearSet].sort((a, b) => b - a);

  const showActions = !estateBilling && canManage;
  const colSpan = showActions ? 6 : 5;

  return (
    <Box>
      {estateBilling && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Fees covered by estate
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2">Payment History</Typography>
        {showActions && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => onRecordPayment && onRecordPayment(currentYear)}
          >
            Add payment
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Year</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reference</TableCell>
              {showActions && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {years.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="text.secondary">
                    No payment history yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {years.map((year) => {
              const yearPayments = (paymentsByYear[year] || [])
                .slice()
                .sort((a, b) => new Date(b.paid_date || 0) - new Date(a.paid_date || 0));

              // Year with no transactions → a single "Unpaid" row.
              if (yearPayments.length === 0) {
                return (
                  <TableRow key={`unpaid-${year}`} hover>
                    <TableCell>{year}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell>
                      <CategoryChip category="UNPAID" />
                    </TableCell>
                    <TableCell>—</TableCell>
                    {showActions && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => onRecordPayment && onRecordPayment(year)}
                        >
                          Record
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              }

              return yearPayments.map((p, idx) => (
                <TableRow key={p.id ?? `${year}-${idx}`} hover>
                  <TableCell>{idx === 0 ? year : ''}</TableCell>
                  <TableCell>{formatDate(p.paid_date)}</TableCell>
                  <TableCell align="right">{formatAmount(p.amount)}</TableCell>
                  <TableCell>
                    <CategoryChip category={p.category || (p.is_paid ? 'PAID' : 'UNPAID')} />
                  </TableCell>
                  <TableCell>
                    {p.payment_ref || '—'}
                    {p.notes ? (
                      <Tooltip title={p.notes}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {p.notes}
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  {showActions && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEditPayment && onEditPayment(p)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onDeletePayment && onDeletePayment(p)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PaymentTimeline;
